/**
 * AdminReglasAgentes.jsx · Hub central de configuración de Reglas de Agentes A1-A6.
 * ─────────────────────────────────────────────────────────────────────────
 * Vista consolidada del "Cerebro de Agentes": una tarjeta por agente con
 * su rol, estado y links directos a configuración y monitoreo.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { Brain, Bot, AlertOctagon, Route, HeartPulse, Target, Settings, Activity, ArrowRight, Trash2, Download, Loader2, ShieldAlert, RefreshCw, Link2, Map, Plus, X } from "lucide-react";
import A2AutopilotSwitch from "../components/A2AutopilotSwitch";
import api from "../lib/api";
import { toast } from "sonner";

const AGENTS = [
  {
    id: "A1",
    name: "Agente A1",
    tagline: "Pre-factibilidad + Stock",
    icon: Brain,
    color: "#0891B2",
    bg: "#CFFAFE",
    role: "Valida cada OT: dirección geocodificable, servicio contratado, stock disponible según receta del tipo de solución.",
    triggers: ["OT ingresa desde Odoo", "Materiales bajan en la bodega del técnico"],
    output: "Marca `a1_pending` con motivo o libera la OT hacia la cola A6.",
    settingsPath: "/admin/a1-settings",
    monitorPath: "/admin/a1-audit",
  },
  {
    id: "A2",
    name: "Agente A2",
    tagline: "Orquestador de Agendamiento",
    icon: Bot,
    color: "#7C3AED",
    bg: "#EDE9FE",
    role: "Ciclo completo A6 → A3 → A5 → A1 → A4. Propone técnico + slot óptimo por OT priorizada. Negocia cita vía WhatsApp (mock).",
    triggers: ["Loop cada 10 min", "Botón manual Dry-Run"],
    output: "Escribe slot en `smart_ot` tras confirmación cliente. Historial en whatsapp_logs.",
    settingsPath: "/admin/brain-config",
    settingsLabel: "Reglas A2",
    monitorPath: "/admin/a2-negotiations",
    monitorLabel: "Negociaciones",
    dryRun: true,
  },
  {
    id: "A3",
    name: "Agente A3",
    tagline: "Bloqueo Eventos Masivos",
    icon: AlertOctagon,
    color: "#E11D48",
    bg: "#FFE4E6",
    role: "Detecta cortes de red / caídas de zona. Bloquea agendamiento en polígonos afectados hasta que se resuelva el evento.",
    triggers: ["Alta manual de evento", "Umbral de OTs por zona"],
    output: "Marca zonas como bloqueadas: A2 no asigna, A6 despriotiza.",
    settingsPath: "/admin/eventos-masivos",
    monitorPath: "/admin/eventos-masivos",
    monitorLabel: "Ver eventos",
  },
  {
    id: "A4",
    name: "Agente A4",
    tagline: "Enrutamiento GPS · TSP",
    icon: Route,
    color: "#2563EB",
    bg: "#DBEAFE",
    role: "Nearest-neighbor TSP diario. Optimiza secuencia de OTs por técnico + mensajería WhatsApp con delay SLA.",
    triggers: ["Cada mañana 05:00", "Botón force-recompute"],
    output: "Escribe orden en `technician_routes` + envía WhatsApp (mock actual).",
    settingsPath: "/admin/rutas-a4",
    monitorPath: "/admin/rutas-a4",
    monitorLabel: "Ver rutas",
  },
  {
    id: "A5",
    name: "Agente A5",
    tagline: "Reasignación Dinámica",
    icon: HeartPulse,
    color: "#DB2777",
    bg: "#FCE7F3",
    role: "Detecta imprevistos (enfermedad, accidente) y breach de SLA en tiempo real. Reasigna OTs a técnico más cercano disponible.",
    triggers: ["Imprevisto reportada por técnico", "Loop SLA cada 5 min"],
    output: "Reescribe `technician_email` + notifica al nuevo técnico.",
    settingsPath: "/admin/imprevistos-a5",
    monitorPath: "/admin/imprevistos-a5",
    monitorLabel: "Ver imprevistos",
  },
  {
    id: "A6",
    name: "Agente A6",
    tagline: "Priorización Dinámica",
    icon: Target,
    color: "#7C3AED",
    bg: "#EDE9FE",
    role: "Recalcula score por OT: SLA restante, VIP, complejidad técnica, distancia. Ordena la cola de despacho.",
    triggers: ["Loop cada 3 min", "Nueva OT válida"],
    output: "Escribe `a6_score` en `master_ot`. A2 consume esta cola.",
    settingsPath: "/admin/cola-a6",
    monitorPath: "/admin/cola-a6",
    monitorLabel: "Ver cola",
  },
];

export default function AdminReglasAgentes() {
  const navigate = useNavigate();

  // v2.9.3 · Panel de Mantenimiento
  const [showCleanupConfirm, setShowCleanupConfirm] = useState(false);
  const [cleanupPreview, setCleanupPreview] = useState(null);       // resultado dry_run
  const [cleanupRunning, setCleanupRunning] = useState(false);
  const [cleanupResult, setCleanupResult] = useState(null);         // resultado del DELETE
  const [cleanupIncludeProtected, setCleanupIncludeProtected] = useState(false);   // v2.9.9
  const [rescueId, setRescueId] = useState("");
  const [rescueRunning, setRescueRunning] = useState(false);
  const [rescueResult, setRescueResult] = useState(null);

  const runCleanupDryRun = async () => {
    setCleanupRunning(true);
    setCleanupResult(null);
    try {
      const { data } = await api.post("/admin/master-ot/cleanup-non-actionable", null, {
        params: { dry_run: true, include_nulls: true, include_protected: cleanupIncludeProtected },
      });
      setCleanupPreview(data);
      setShowCleanupConfirm(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al analizar la limpieza");
    } finally {
      setCleanupRunning(false);
    }
  };

  const runCleanupExecute = async () => {
    setCleanupRunning(true);
    try {
      const { data } = await api.post("/admin/master-ot/cleanup-non-actionable", null, {
        params: { dry_run: false, include_nulls: true, include_protected: cleanupIncludeProtected },
      });
      setCleanupResult(data);
      toast.success(`Limpieza aplicada · ${data.deleted} OTs eliminadas de master_ot`);
      setShowCleanupConfirm(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al ejecutar la limpieza");
    } finally {
      setCleanupRunning(false);
    }
  };

  const runRescue = async () => {
    const id = (rescueId || "").trim();
    if (!/^\d+$/.test(id)) {
      toast.error("Ingresa un ID numérico válido (ej. 998137)");
      return;
    }
    setRescueRunning(true);
    setRescueResult(null);
    try {
      const { data } = await api.post(`/admin/odoo-live/force-resync/${id}`);
      setRescueResult(data);
      if (data.upsert_ok) {
        toast.success(`OT ${id} rescatada y guardada en master_ot`);
      } else if (data.found_in_odoo === false) {
        toast.warning(`OT ${id} NO existe en Odoo`);
      } else if (data.upsert_error) {
        toast.error(`Odoo OK, pero UPSERT falló: ${data.upsert_error.substring(0, 100)}`);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      setRescueResult({ error: detail });
      toast.error(`Error: ${detail}`);
    } finally {
      setRescueRunning(false);
    }
  };

  // v2.9.4 · Reset bitácora A2 + Backfill technician_id
  const [resetRunning, setResetRunning] = useState(false);
  const [backfillRunning, setBackfillRunning] = useState(false);
  const [maintExtraResult, setMaintExtraResult] = useState(null);

  // v2.9.8 · Gestión odoo_stage_map
  const [stageMapOpen, setStageMapOpen] = useState(false);
  const [stageMapItems, setStageMapItems] = useState([]);
  const [stageMapLoading, setStageMapLoading] = useState(false);
  const [newStageId, setNewStageId] = useState("");
  const [newStageName, setNewStageName] = useState("");

  const loadStageMap = async () => {
    setStageMapLoading(true);
    try {
      const { data } = await api.get("/admin/odoo-stage-map");
      setStageMapItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando mapa");
    } finally {
      setStageMapLoading(false);
    }
  };

  const openStageMap = async () => {
    setStageMapOpen(true);
    await loadStageMap();
  };

  const addStageMapping = async () => {
    const sid = parseInt(newStageId, 10);
    const nm = (newStageName || "").trim();
    if (!Number.isInteger(sid) || !nm) {
      toast.error("Ingresa stage_id numérico y nombre");
      return;
    }
    try {
      await api.post("/admin/odoo-stage-map", { stage_id: sid, stage_name: nm });
      toast.success(`Mapeo ${sid} → ${nm} guardado`);
      setNewStageId("");
      setNewStageName("");
      await loadStageMap();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    }
  };

  const deleteStageMapping = async (sid) => {
    if (!window.confirm(`¿Eliminar mapeo del stage_id ${sid}?`)) return;
    try {
      await api.delete(`/admin/odoo-stage-map/${sid}`);
      toast.success(`Mapeo ${sid} eliminado`);
      await loadStageMap();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al eliminar");
    }
  };

  const runResetEvents = async () => {
    if (!window.confirm("¿Confirmas TRUNCATE de a2_autopilot_events? Se pierde el historial.")) return;
    setResetRunning(true);
    setMaintExtraResult(null);
    try {
      const { data } = await api.post("/admin/a2/reset-events");
      setMaintExtraResult({ kind: "reset", data });
      toast.success(`Bitácora A2 limpia · ${data.deleted_rows} eventos eliminados`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al resetear eventos");
    } finally {
      setResetRunning(false);
    }
  };

  const runBackfill = async () => {
    setBackfillRunning(true);
    setMaintExtraResult(null);
    try {
      const { data } = await api.post("/admin/master-ot/backfill-tech-ids");
      setMaintExtraResult({ kind: "backfill", data });
      toast.success(`Vinculados ${data.updated} technician_id nuevos · huérfanos restantes: ${data.assignments_still_orphan}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al vincular técnicos");
    } finally {
      setBackfillRunning(false);
    }
  };

  // v2.9.9.1 · Zombie cleanup (cross-check con Odoo)
  const [zombieRunning, setZombieRunning] = useState(false);

  const runZombieCleanup = async () => {
    setZombieRunning(true);
    setMaintExtraResult(null);
    try {
      const dry = await api.post("/admin/master-ot/zombie-cleanup", null, { params: { dry_run: true } });
      const d = dry.data;
      if (!window.confirm(
        `Zombies detectados: ${d.zombies_found} de ${d.master_ot_before}.\n\n` +
        `• fsm_done=true: ${d.reasons_breakdown.fsm_done_true}\n` +
        `• stage_id ≠ ${JSON.stringify(d.allowed_stage_ids)}: ${d.reasons_breakdown.stage_not_in_whitelist}\n` +
        `• sin registro en Odoo: ${d.reasons_breakdown.missing_in_odoo}\n\n` +
        `¿Ejecutar DELETE (irreversible)?`
      )) { setZombieRunning(false); return; }
      const { data } = await api.post("/admin/master-ot/zombie-cleanup", null, { params: { dry_run: false } });
      setMaintExtraResult({ kind: "zombie", data });
      toast.success(`Zombis eliminados: ${data.deleted} · master_ot ahora: ${data.master_ot_after}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error zombie cleanup");
    } finally {
      setZombieRunning(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-5" data-testid="reglas-agentes-page">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full mb-2"
                 style={{ background: "#F3E8FF", border: "1px solid #A855F7" }}>
              <Brain size={11} className="text-purple-700" />
              <span className="text-[10px] font-semibold uppercase tracking-widest text-purple-700">
                Cerebro de Agentes · Configuración
              </span>
            </div>
            <h1 className="text-3xl font-bold" style={{ color: "#0A3747" }}>Reglas de los Agentes Autónomos</h1>
            <p className="mt-1 text-sm text-gray-500 max-w-2xl">
              Cada agente ejecuta una responsabilidad específica en el ciclo de vida de la OT.
              Ajusta los parámetros o abre el panel de monitoreo para revisar su estado actual.
            </p>
          </div>
        </div>

        {/* A2 Autopilot Master Switch · v2.8.9.0 */}
        <div data-testid="reglas-a2-autopilot">
          <A2AutopilotSwitch />
        </div>

        {/* v3.8.0 · Tarjeta prominente Configuración de Reglas → /admin/brain-config */}
        <button
          onClick={() => navigate("/admin/brain-config")}
          className="w-full text-left neu-card p-4 border-2 border-purple-300 bg-gradient-to-r from-purple-50 to-white hover:from-purple-100 transition group"
          data-testid="card-brain-config-open"
        >
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-purple-100 text-purple-700 flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition">
              <Settings size={26} strokeWidth={2} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-mono uppercase tracking-widest font-bold text-purple-700 mb-0.5">
                Sistema · Palancas Globales
              </div>
              <div className="font-bold text-lg text-[#0A3747]">Configuración de Reglas</div>
              <div className="text-xs text-gray-600 mt-0.5">
                Edita el buffer de traslado, horizonte, batch y máx. días por técnico del A2.
                Los cambios se aplican en el próximo ciclo <b>sin redeploy</b>.
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2 text-[10px] font-mono">
                <span className="px-1.5 py-0.5 rounded bg-purple-100 text-purple-800">A2_TRAVEL_BUFFER_MIN</span>
                <span className="px-1.5 py-0.5 rounded bg-purple-100 text-purple-800">MAX_DAYS_AHEAD</span>
                <span className="px-1.5 py-0.5 rounded bg-purple-100 text-purple-800">MAX_DAYS_PER_TECH</span>
                <span className="px-1.5 py-0.5 rounded bg-purple-100 text-purple-800">BATCH_SIZE_A2</span>
              </div>
            </div>
            <ArrowRight size={22} className="text-purple-500 flex-shrink-0 group-hover:translate-x-1 transition" />
          </div>
        </button>

        {/* Grid de agentes */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {AGENTS.map((a) => (
            <div
              key={a.id}
              className="neu-card p-4 flex flex-col gap-3"
              data-testid={`agent-card-${a.id}`}
            >
              {/* Header con icono */}
              <div className="flex items-start gap-3">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: a.bg }}
                >
                  <a.icon size={22} style={{ color: a.color }} strokeWidth={2.2} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-[10px] font-mono uppercase tracking-widest font-bold" style={{ color: a.color }}>
                      {a.id}
                    </div>
                    {a.dryRun && (
                      <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-300">
                        Dry-Run
                      </span>
                    )}
                  </div>
                  <div className="font-bold text-sm truncate" style={{ color: "#0A3747" }}>{a.name}</div>
                  <div className="text-[11px] text-gray-500 truncate">{a.tagline}</div>
                </div>
              </div>

              {/* Rol */}
              <p className="text-xs text-gray-700 leading-relaxed">{a.role}</p>

              {/* Triggers + Output */}
              <div className="space-y-1.5 border-t border-gray-100 pt-2">
                <div>
                  <div className="text-[9px] uppercase tracking-widest font-bold text-gray-400 mb-0.5">Se dispara con</div>
                  <div className="flex flex-wrap gap-1">
                    {a.triggers.map((t, i) => (
                      <span key={i} className="px-1.5 py-0.5 rounded text-[10px] bg-gray-100 text-gray-700">{t}</span>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-widest font-bold text-gray-400 mb-0.5">Resultado</div>
                  <p className="text-[11px] text-gray-600 leading-snug">{a.output}</p>
                </div>
              </div>

              {/* Acciones */}
              <div className="flex gap-2 mt-auto pt-2 border-t border-gray-100">
                {a.settingsPath && a.settingsPath !== a.monitorPath && (
                  <button
                    onClick={() => navigate(a.settingsPath)}
                    className="neu-btn flex-1 text-xs"
                    data-testid={`btn-config-${a.id}`}
                  >
                    <Settings size={12} /> {a.settingsLabel || "Configurar"}
                  </button>
                )}
                <button
                  onClick={() => navigate(a.monitorPath)}
                  className="neu-btn neu-btn-primary flex-1 text-xs"
                  data-testid={`btn-monitor-${a.id}`}
                >
                  <Activity size={12} /> {a.monitorLabel || "Monitorear"} <ArrowRight size={12} />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Nota informativa */}
        <div className="neu-card p-3 flex items-start gap-2 bg-purple-50/40 border border-purple-100">
          <Brain size={14} className="text-purple-700 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-purple-900 leading-relaxed">
            <b>Flujo:</b> Odoo → <b>A1</b> valida → <b>A6</b> prioriza → <b>A2</b> orquesta agendamiento (consulta A3 zonas
            bloqueadas + A5 técnicos con imprevisto) → <b>A4</b> optimiza ruta del día. A5 reasigna dinámicamente si hay eventos.
          </p>
        </div>

        {/* v2.9.3 · Panel de Mantenimiento */}
        <div
          className="neu-card p-4 space-y-4 border-2 border-amber-200"
          style={{ background: "linear-gradient(135deg, #FFFBEB 0%, #FEF3C7 100%)" }}
          data-testid="cerebros-mantenimiento"
        >
          <div className="flex items-center gap-2">
            <ShieldAlert size={18} className="text-amber-700" />
            <div>
              <h2 className="text-lg font-bold text-amber-900">Mantenimiento del sistema</h2>
              <p className="text-xs text-amber-800">Acciones directas sobre <code>master_ot</code>. Requieren rol <b>super_admin</b>.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Bloque 1: Limpieza masiva */}
            <div className="bg-white rounded-lg p-3 border border-amber-200 space-y-2">
              <div className="flex items-center gap-1.5">
                <Trash2 size={13} className="text-red-600" />
                <div className="text-sm font-semibold text-slate-800">Limpieza masiva de OTs</div>
              </div>
              <p className="text-[11px] text-gray-600 leading-snug">
                Elimina de <code>master_ot</code> todas las OTs cuyo <code>stage_name</code> NO esté en <b>Pendiente/Nuevo</b>,
                incluyendo las de stage desconocido. <br />
                <b>Protege</b> las asignadas/agendadas/confirmadas/en_ruta.
              </p>
              <label className="flex items-center gap-1.5 text-[11px] text-red-800">
                <input
                  type="checkbox"
                  checked={cleanupIncludeProtected}
                  onChange={(e) => setCleanupIncludeProtected(e.target.checked)}
                  className="accent-red-600"
                  data-testid="cleanup-include-protected"
                />
                Incluir también OTs asignadas/agendadas por A2 (limpieza total tras corregir el mapa)
              </label>
              <div className="flex items-center gap-2 pt-1">
                <button
                  onClick={runCleanupDryRun}
                  disabled={cleanupRunning}
                  className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                  data-testid="btn-cleanup-master-ot"
                >
                  {cleanupRunning ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                  Analizar y ejecutar limpieza
                </button>
              </div>
              {cleanupResult && (
                <div className="mt-2 text-[11px] bg-emerald-50 border border-emerald-300 rounded p-2 text-emerald-900" data-testid="cleanup-result">
                  <div><b>✅ Limpieza aplicada:</b></div>
                  <div>Antes: {cleanupResult.master_ot_before} · Después: {cleanupResult.master_ot_after}</div>
                  <div>Eliminadas: <b>{cleanupResult.deleted}</b></div>
                </div>
              )}
            </div>

            {/* Bloque 2: Rescate OT desde Odoo */}
            <div className="bg-white rounded-lg p-3 border border-amber-200 space-y-2">
              <div className="flex items-center gap-1.5">
                <Download size={13} className="text-blue-600" />
                <div className="text-sm font-semibold text-slate-800">Rescate manual de OT</div>
              </div>
              <p className="text-[11px] text-gray-600 leading-snug">
                Fuerza el fetch de una OT desde Odoo por su <b>ID (odoo_task_id)</b> y aplica UPSERT en <code>master_ot</code>.
                Útil cuando el sync incremental la saltó.
              </p>
              <div className="flex items-center gap-2 pt-1">
                <input
                  type="number"
                  min="1"
                  value={rescueId}
                  onChange={(e) => setRescueId(e.target.value)}
                  placeholder="ID Odoo (ej. 998137)"
                  className="flex-1 px-2 py-1.5 text-xs border border-slate-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 font-mono"
                  data-testid="input-rescue-ot-id"
                  onKeyDown={(e) => { if (e.key === "Enter") runRescue(); }}
                />
                <button
                  onClick={runRescue}
                  disabled={rescueRunning || !rescueId}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                  data-testid="btn-rescue-ot"
                >
                  {rescueRunning ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
                  Rescatar ahora
                </button>
              </div>
              {rescueResult && (
                <div className="mt-2 text-[11px] bg-slate-50 border border-slate-300 rounded p-2 font-mono max-h-40 overflow-auto" data-testid="rescue-result">
                  {rescueResult.error ? (
                    <div className="text-red-700"><b>Error:</b> {rescueResult.error}</div>
                  ) : rescueResult.found_in_odoo === false ? (
                    <div className="text-amber-800"><b>⚠ No existe en Odoo:</b> {rescueResult.message}</div>
                  ) : rescueResult.upsert_ok ? (
                    <div className="text-emerald-800">
                      <div><b>✅ Rescatada</b></div>
                      <div>OT #{rescueResult.odoo_task_id} · {rescueResult.odoo_payload?.client_name}</div>
                      <div>Stage: <b>{rescueResult.odoo_payload?.stage_name || "—"}</b></div>
                      <div>Task Number: {rescueResult.odoo_payload?.task_number || "—"}</div>
                      <div>Plan: {rescueResult.odoo_payload?.plan_name || "—"}</div>
                      <div>Branch: {rescueResult.odoo_payload?.branch_name || "—"}</div>
                    </div>
                  ) : (
                    <div className="text-red-700">
                      <div><b>❌ UPSERT falló:</b></div>
                      <div>{rescueResult.upsert_error}</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Bloque 3: Reset Bitácora A2 */}
            <div className="bg-white rounded-lg p-3 border border-amber-200 space-y-2">
              <div className="flex items-center gap-1.5">
                <RefreshCw size={13} className="text-purple-600" />
                <div className="text-sm font-semibold text-slate-800">Reset bitácora A2</div>
              </div>
              <p className="text-[11px] text-gray-600 leading-snug">
                TRUNCATE de <code>a2_autopilot_events</code>. Limpia la métrica &quot;Auto-agendamientos 24h&quot; del histórico viejo.
              </p>
              <button
                onClick={runResetEvents}
                disabled={resetRunning}
                className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50"
                data-testid="btn-reset-a2-events"
              >
                {resetRunning ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
                Resetear historial A2
              </button>
              {maintExtraResult?.kind === "reset" && (
                <div className="text-[11px] bg-purple-50 border border-purple-300 rounded p-2 text-purple-900 mt-2">
                  ✅ {maintExtraResult.data.deleted_rows} eventos eliminados
                </div>
              )}
            </div>

            {/* Bloque 4: Backfill technician_id */}
            <div className="bg-white rounded-lg p-3 border border-amber-200 space-y-2">
              <div className="flex items-center gap-1.5">
                <Link2 size={13} className="text-emerald-600" />
                <div className="text-sm font-semibold text-slate-800">Vincular técnicos huérfanos</div>
              </div>
              <p className="text-[11px] text-gray-600 leading-snug">
                Match case-insensitive de <code>assigned_tech_email</code> ↔ <code>users.email</code>. Vincula OTs con técnico asignado
                que no salían con nombre en el listado.
              </p>
              <button
                onClick={runBackfill}
                disabled={backfillRunning}
                className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-50"
                data-testid="btn-backfill-tech-ids"
              >
                {backfillRunning ? <Loader2 size={13} className="animate-spin" /> : <Link2 size={13} />}
                Vincular technician_id ahora
              </button>
              {maintExtraResult?.kind === "backfill" && (
                <div className="text-[11px] bg-emerald-50 border border-emerald-300 rounded p-2 text-emerald-900 mt-2">
                  ✅ Actualizadas: <b>{maintExtraResult.data.updated}</b><br />
                  Asignadas totales: {maintExtraResult.data.assignments_total} · Huérfanos: {maintExtraResult.data.assignments_still_orphan}
                </div>
              )}
            </div>

            {/* v2.9.8 · Bloque 5: Mapa de stages Odoo */}
            <div className="bg-white rounded-lg p-3 border border-amber-200 space-y-2">
              <div className="flex items-center gap-1.5">
                <Map size={13} className="text-cyan-600" />
                <div className="text-sm font-semibold text-slate-800">Mapa de stages Odoo</div>
              </div>
              <p className="text-[11px] text-gray-600 leading-snug">
                Traduce <code>stage_id</code> Odoo → nombre UI (ej. 22 → &quot;Nuevo&quot;, 17 → &quot;Agendado&quot;).
                Sin mapeo, el Kanban no puede etiquetar la columna.
              </p>
              <button
                onClick={openStageMap}
                className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-cyan-600 text-white rounded-md hover:bg-cyan-700"
                data-testid="btn-open-stage-map"
              >
                <Map size={13} />
                Gestionar mapeo
              </button>
            </div>

            {/* v2.9.9.1 · Bloque 6: Zombie cleanup */}
            <div className="bg-white rounded-lg p-3 border border-amber-200 space-y-2">
              <div className="flex items-center gap-1.5">
                <ShieldAlert size={13} className="text-orange-600" />
                <div className="text-sm font-semibold text-slate-800">Barrido de zombis</div>
              </div>
              <p className="text-[11px] text-gray-600 leading-snug">
                Cross-check con Odoo: elimina OTs con <code>fsm_done=true</code>, <code>stage_id</code> fuera de whitelist,
                o que ya no existen en el ERP.
              </p>
              <button
                onClick={runZombieCleanup}
                disabled={zombieRunning}
                className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50"
                data-testid="btn-zombie-cleanup"
              >
                {zombieRunning ? <Loader2 size={13} className="animate-spin" /> : <ShieldAlert size={13} />}
                Ejecutar barrido
              </button>
              {maintExtraResult?.kind === "zombie" && (
                <div className="text-[11px] bg-orange-50 border border-orange-300 rounded p-2 text-orange-900 mt-2">
                  ✅ Zombis eliminados: <b>{maintExtraResult.data.deleted}</b><br />
                  master_ot: {maintExtraResult.data.master_ot_before} → <b>{maintExtraResult.data.master_ot_after}</b><br />
                  <span className="text-[10px]">
                    fsm_done: {maintExtraResult.data.reasons_breakdown.fsm_done_true} ·
                    stage≠wl: {maintExtraResult.data.reasons_breakdown.stage_not_in_whitelist} ·
                    missing: {maintExtraResult.data.reasons_breakdown.missing_in_odoo}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* v2.9.8 · Modal · Gestión odoo_stage_map */}
        {stageMapOpen && (
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={(e) => { if (e.target === e.currentTarget) setStageMapOpen(false); }}
            data-testid="stage-map-modal"
          >
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-5 space-y-3">
              <div className="flex items-center gap-2">
                <Map size={18} className="text-cyan-700" />
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900">Mapa de stages Odoo</h3>
                  <p className="text-xs text-gray-500">Traduce stage_id numérico → nombre UI</p>
                </div>
                <button
                  onClick={() => setStageMapOpen(false)}
                  className="p-1 text-gray-500 hover:text-gray-900 rounded"
                  aria-label="Cerrar"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Nuevo mapeo */}
              <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-3 space-y-2">
                <div className="text-xs font-semibold text-cyan-900">Agregar mapeo</div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={newStageId}
                    onChange={(e) => setNewStageId(e.target.value)}
                    placeholder="stage_id (ej. 22)"
                    className="w-28 px-2 py-1 text-xs border border-cyan-300 rounded font-mono"
                    data-testid="stage-map-input-id"
                  />
                  <input
                    type="text"
                    value={newStageName}
                    onChange={(e) => setNewStageName(e.target.value)}
                    placeholder="Nombre (ej. Nuevo)"
                    className="flex-1 px-2 py-1 text-xs border border-cyan-300 rounded"
                    onKeyDown={(e) => { if (e.key === "Enter") addStageMapping(); }}
                    data-testid="stage-map-input-name"
                  />
                  <button
                    onClick={addStageMapping}
                    className="p-1.5 bg-cyan-600 text-white rounded hover:bg-cyan-700"
                    data-testid="stage-map-add"
                  >
                    <Plus size={14} />
                  </button>
                </div>
              </div>

              {/* Lista actual */}
              <div className="border border-slate-200 rounded-lg max-h-64 overflow-auto">
                {stageMapLoading ? (
                  <div className="p-4 text-center text-xs text-slate-500">
                    <Loader2 size={16} className="animate-spin inline mr-2" />
                    Cargando...
                  </div>
                ) : stageMapItems.length === 0 ? (
                  <div className="p-4 text-center text-xs text-slate-500">Sin mapeos.</div>
                ) : (
                  <table className="w-full text-xs" data-testid="stage-map-table">
                    <thead className="bg-slate-100 text-slate-700 font-semibold">
                      <tr>
                        <th className="px-2 py-1.5 text-left">stage_id</th>
                        <th className="px-2 py-1.5 text-left">Nombre UI</th>
                        <th className="px-2 py-1.5 text-left">Mapeado por</th>
                        <th className="px-2 py-1.5"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {stageMapItems.map((it) => (
                        <tr key={it.stage_id} className="border-t border-slate-200 hover:bg-slate-50">
                          <td className="px-2 py-1.5 font-mono">{it.stage_id}</td>
                          <td className="px-2 py-1.5">{it.stage_name}</td>
                          <td className="px-2 py-1.5 text-slate-500 truncate max-w-[120px]">{it.mapped_by}</td>
                          <td className="px-2 py-1.5 text-right">
                            <button
                              onClick={() => deleteStageMapping(it.stage_id)}
                              className="p-1 text-red-600 hover:bg-red-100 rounded"
                              data-testid={`stage-map-del-${it.stage_id}`}
                            >
                              <Trash2 size={12} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <div className="text-[11px] text-slate-500 italic">
                Tip: mapea también los stage_id que aparezcan sin nombre en Flow Monitor.
              </div>
            </div>
          </div>
        )}

        {/* v2.9.3 · Modal confirmación limpieza */}
        {showCleanupConfirm && cleanupPreview && (
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={(e) => { if (e.target === e.currentTarget && !cleanupRunning) setShowCleanupConfirm(false); }}
            data-testid="cleanup-confirm-modal"
          >
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-6 space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <Trash2 size={20} className="text-red-700" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">¿Confirmas la limpieza masiva?</h3>
                  <p className="text-xs text-gray-500">Esta acción es <b>irreversible</b>.</p>
                </div>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm space-y-1">
                <div className="text-red-900">
                  Se eliminarán <b className="text-lg">{cleanupPreview.candidates_to_delete}</b> OTs de{" "}
                  <b>{cleanupPreview.master_ot_before}</b> totales.
                </div>
                <div className="text-xs text-red-800">
                  Whitelist (se conservan): <b>{cleanupPreview.allowed_stages?.join(", ")}</b>
                </div>
                <div className="text-xs text-red-800">
                  Protegidas por estado interno: {cleanupPreview.protected_internal_statuses?.length} estados A2
                </div>
              </div>

              {cleanupPreview.stage_breakdown?.length > 0 && (
                <div className="border border-slate-200 rounded-lg p-2 max-h-40 overflow-auto text-xs">
                  <div className="font-semibold text-slate-700 mb-1">Detalle por stage:</div>
                  {cleanupPreview.stage_breakdown.map((s, i) => (
                    <div key={i} className="flex justify-between font-mono text-[11px] py-0.5">
                      <span>{s.stage_name || <i className="text-gray-500">(sin stage)</i>}</span>
                      <span className="text-slate-600">{s.n}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  onClick={() => setShowCleanupConfirm(false)}
                  disabled={cleanupRunning}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                  data-testid="cleanup-cancel"
                >
                  Cancelar
                </button>
                <button
                  onClick={runCleanupExecute}
                  disabled={cleanupRunning}
                  className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-1.5"
                  data-testid="cleanup-confirm"
                >
                  {cleanupRunning ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                  Sí, eliminar {cleanupPreview.candidates_to_delete} OTs
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
